import { useState } from 'react';
import { VEHICLES } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRight, Zap, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const BookingWidget = ({ compact = false }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        pickup_city: '',
        pickup_address: '',
        dropoff_city: '',
        dropoff_address: '',
        pickup_date: new Date().toISOString().slice(0, 10),
        pickup_time: '12:00',
        passengers: 1,
        vehicle_type: VEHICLES[0].id,
    });
    const currentVehicle = VEHICLES.find(v => v.id === form.vehicle_type) || VEHICLES[0];

    const continueToFull = () => {
        if (!form.pickup_city || !form.pickup_address || !form.dropoff_city || !form.dropoff_address) {
            toast.error('Veuillez renseigner les villes et adresses');
            return;
        }
        navigate('/reservation', { state: { prefill: form } });
    };

    return (
        <div data-testid="booking-widget" className="bg-black/60 backdrop-blur-2xl border border-white/10 p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">Réservation rapide</div>
                    <h3 className="font-display text-3xl text-[#F8F8F5] mt-2 font-light">Votre course en quelques secondes</h3>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Ville de départ</label>
                    <input
                        data-testid="booking-pickup-city-input"
                        value={form.pickup_city}
                        onChange={(e) => setForm({ ...form, pickup_city: e.target.value })}
                        placeholder="Ex: Paris"
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Ville d'arrivée</label>
                    <input
                        data-testid="booking-dropoff-city-input"
                        value={form.dropoff_city}
                        onChange={(e) => setForm({ ...form, dropoff_city: e.target.value })}
                        placeholder="Ex: Lyon"
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Adresse de départ</label>
                    <input
                        data-testid="booking-pickup-input"
                        value={form.pickup_address}
                        onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                        placeholder="Adresse ou aéroport"
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Adresse d'arrivée</label>
                    <input
                        data-testid="booking-dropoff-input"
                        value={form.dropoff_address}
                        onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })}
                        placeholder="Adresse d'arrivée"
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Date</label>
                    <input
                        data-testid="booking-date-input"
                        type="date"
                        value={form.pickup_date}
                        onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors [color-scheme:dark]"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Heure</label>
                    <input
                        data-testid="booking-time-input"
                        type="time"
                        value={form.pickup_time}
                        onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors [color-scheme:dark]"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Passagers</label>
                    <input
                        data-testid="booking-passengers-input"
                        type="number" min="1" max={currentVehicle.maxPax}
                        value={form.passengers}
                        onChange={(e) => setForm({ ...form, passengers: Number(e.target.value) })}
                        className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none transition-colors"
                    />
                </div>
            </div>

            <div className="mt-8">
                <label className="text-xs uppercase tracking-[0.2em] text-[#A3A39D] mb-4 block">Véhicule</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {VEHICLES.map(v => (
                        <button
                            key={v.id}
                            data-testid={`vehicle-${v.id}`}
                            onClick={() => setForm({ ...form, vehicle_type: v.id, passengers: Math.min(form.passengers, v.maxPax) })}
                            className={`p-5 border text-left transition-colors flex items-start gap-3 ${
                                form.vehicle_type === v.id
                                    ? 'border-[#C5A059] bg-[#C5A059]/5'
                                    : 'border-[#222] hover:border-[#C5A059]/40'
                            }`}
                        >
                            {v.id === 'tesla_model_y'
                                ? <Zap size={18} className="text-[#C5A059] mt-0.5" />
                                : <Users size={18} className="text-[#C5A059] mt-0.5" />}
                            <div>
                                <div className="text-sm font-medium text-[#F8F8F5]">{v.label}</div>
                                <div className="text-xs text-[#A3A39D] mt-1">{v.desc}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6 pt-8 border-t border-white/5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#A3A39D]">Acompte en ligne</div>
                    <div className="font-display text-4xl text-[#C5A059] mt-1" data-testid="deposit-amount">10,00 €</div>
                    <div className="text-xs text-[#A3A39D] mt-1">Non remboursable · déduit du prix de la course</div>
                </div>
                <button
                    data-testid="booking-continue-btn"
                    onClick={continueToFull}
                    className="bg-[#C5A059] text-black px-10 py-4 uppercase tracking-[0.25em] text-xs font-medium hover:bg-[#DCC283] transition-colors flex items-center justify-center gap-3"
                >
                    Continuer <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};
